// src/pages/project/ProjectSettingsDangerPage.tsx
// Danger zone: ArchiveProject / RestoreProject / CloneProject / DeleteProject.

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, Input, Card, CardContent, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field } from '@/features/project/ProjectModal';
import type { Entity } from '@core/types/entity';

export const ProjectSettingsDangerPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProject();
  const { t } = useI18n();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id && !store.currentItem) store.GetProject(id);
  }, [id]);

  const item = store.currentItem as never as { entity?: Entity; status?: string } | null;
  const projectEntity = item?.entity;
  const isArchived = item?.status === 'PROJECT_STATUS_ARCHIVED';

  const archive = async () => {
    if (!id || !projectEntity) return;
    setBusy(true);
    try {
      await store.ArchiveProject(id, { project: projectEntity } as never);
      await store.GetProject(id);
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!id || !projectEntity) return;
    setBusy(true);
    try {
      await store.RestoreProject(id, { project: projectEntity } as never);
      await store.GetProject(id);
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await store.DeleteProject(id);
      navigate('/projects');
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  if (store.isLoading && !item) {
    return (
      <div className="max-w-2xl space-y-6">
        <div className="h-7 w-40 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded-lg bg-muted/50" />
      </div>
    );
  }

  if (!item || !projectEntity) return null;

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-destructive">{t('project.dangerZone.title')}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('project.dangerZone.description')}</p>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <Card>
        <CardContent className="space-y-4 p-6">
          {/* Archive / Restore */}
          <DangerRow
            title={isArchived ? t('project.restore') : t('project.archive')}
            description={isArchived ? t('project.dangerZone.restoreDescription') : t('project.dangerZone.archiveDescription')}
            action={
              <Button size="sm" variant="secondary" onClick={isArchived ? restore : archive} disabled={busy}>
                {isArchived ? t('project.restore') : t('project.archive')}
              </Button>
            }
          />

          {/* Clone */}
          <DangerRow
            title={t('project.clone.action')}
            description={t('project.dangerZone.cloneDescription')}
            action={
              <Button size="sm" variant="secondary" onClick={() => setShowClone(true)} disabled={busy}>
                {t('project.clone.action')}
              </Button>
            }
          />
        </CardContent>
      </Card>

      {/* Delete */}
      <Card className="border-destructive/30">
        <CardContent className="p-6">
          <DangerRow
            danger
            title={t('project.delete')}
            description={t('project.dangerZone.deleteDescription')}
            action={
              <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
                {t('project.delete')}
              </Button>
            }
          />
        </CardContent>
      </Card>

      {showClone && (
        <CloneDialog
          projectEntity={projectEntity}
          onClose={() => setShowClone(false)}
          onCloned={(newId) => {
            setShowClone(false);
            if (newId) navigate(`/projects/${newId}`);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('project.deleteConfirm')}
        description={t('project.dangerZone.deleteDescription')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
});

function DangerRow({
  title,
  description,
  action,
  danger,
}: {
  title: string;
  description: string;
  action: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-lg border p-4 ${
        danger ? 'border-destructive/30 bg-destructive/5' : 'border-border'
      }`}
    >
      <div className="min-w-0">
        <p className={`text-sm font-medium ${danger ? 'text-destructive' : 'text-foreground'}`}>{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}

function CloneDialog({
  projectEntity,
  onClose,
  onCloned,
}: {
  projectEntity: Entity;
  onClose: () => void;
  onCloned: (newId: string | null) => void;
}) {
  const { t } = useI18n();
  const store = useProject();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [includeBoards, setIncludeBoards] = useState(true);
  const [includeEpics, setIncludeEpics] = useState(true);
  const [includeLabels, setIncludeLabels] = useState(true);
  const [includeMembers, setIncludeMembers] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !key.trim()) return;
    setBusy(true);
    try {
      const res = await store.CloneProject(projectEntity.id, {
        project: projectEntity,
        new_name: name.trim(),
        new_key: key.trim(),
        include_boards: includeBoards,
        include_epics: includeEpics,
        include_labels: includeLabels,
        include_members: includeMembers,
        include_saved_views: false,
      } as never);
      onCloned(res.project?.entity?.id ?? null);
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  const Toggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-input" />
      {label}
    </label>
  );

  return (
    <ProjectModal
      open
      title={t('project.clone.action')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim() || !key.trim()}>
            {t('project.clone.action')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.clone.newName')} htmlFor="clone-name">
          <Input id="clone-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.clone.newKey')} htmlFor="clone-key">
          <Input id="clone-key" value={key} onChange={(e) => setKey(e.target.value.toUpperCase())} className="w-40" />
        </Field>
        <div className="space-y-2">
          <Toggle label={t('project.clone.includeBoards')} checked={includeBoards} onChange={setIncludeBoards} />
          <Toggle label={t('project.clone.includeEpics')} checked={includeEpics} onChange={setIncludeEpics} />
          <Toggle label={t('project.clone.includeLabels')} checked={includeLabels} onChange={setIncludeLabels} />
          <Toggle label={t('project.clone.includeMembers')} checked={includeMembers} onChange={setIncludeMembers} />
        </div>
      </div>
    </ProjectModal>
  );
}

export default ProjectSettingsDangerPage;
